export const PERIODS = ['3', '6', '12'] as const
export type Period = (typeof PERIODS)[number]

export type KpiFormat = 'money' | 'count' | 'pct' | 'days' | 'hours'

export type Kpi = {
  key: string
  value: number
  format: KpiFormat
  deltaPct: number | null
  goodWhenUp: boolean
  spark: number[]
}

export type MonthPoint<K extends string> = { month: string } & Record<K, number>

export type Share = { key: string; label: string; value: number; sub?: string }

export type Insight = { tone: 'good' | 'warning' | 'bad' | 'info'; text: string }

export type OverviewReport = {
  kpis: Kpi[]
  revenue: MonthPoint<'income' | 'expense' | 'net'>[]
  pipeline: Share[]
  topCustomers: Share[]
  activity: MonthPoint<'meetings' | 'offers' | 'tickets'>[]
  insights: Insight[]
}

export type RepRow = { id: string; name: string; title: string; customers: number; meetings: number; offers: number; won: number; wonValue: number; winRate: number | null }

export type SalesReport = {
  kpis: Kpi[]
  monthly: MonthPoint<'sent' | 'won' | 'lost'>[]
  wonValue: MonthPoint<'value'>[]
  funnel: { key: string; label: string; count: number; conversion: number | null }[]
  reps: RepRow[]
  regions: Share[]
  products: Share[]
  lostReasons: Share[]
}

export type AgingBucket = { key: '0_30' | '31_60' | '61_90' | '90_plus' | 'not_due'; value: number; count: number }

export type FinanceReport = {
  kpis: Kpi[]
  monthly: MonthPoint<'income' | 'expense' | 'net'>[]
  cash: MonthPoint<'balance'>[]
  incomeMix: Share[]
  expenseMix: Share[]
  aging: AgingBucket[]
  receivables: Share[]
}

export type CustomerReport = {
  kpis: Kpi[]
  growth: MonthPoint<'newCustomers' | 'churned'>[]
  byProduct: Share[]
  bySector: Share[]
  byRegion: Share[]
  modules: Share[]
  tickets: MonthPoint<'opened' | 'resolved'>[]
  ticketStatus: Share[]
  ticketPriority: Share[]
  renewals: { id: string; customer: string; kind: string; dueOn: string; amount: number; daysLeft: number; status: string }[]
}

export type ProjectRow = { id: string; name: string; total: number; done: number; inProgress: number; review: number; todo: number; overdue: number; estimate: number; spent: number; progress: number; installations: number }

export type OperationsReport = {
  kpis: Kpi[]
  throughput: MonthPoint<'created' | 'completed'>[]
  projects: ProjectRow[]
  workload: { id: string; name: string; open: number; overdue: number; hours: number }[]
  installations: { id: string; project: string; customer: string; status: string; progress: number; targetDate: string; blockers: number }[]
  byType: Share[]
}

export type HrReport = {
  kpis: Kpi[]
  headcount: MonthPoint<'headcount' | 'hires' | 'exits'>[]
  byDepartment: Share[]
  payroll: { department: string; gross: number; net: number; people: number }[]
  employment: Share[]
  tenure: Share[]
  locations: Share[]
}
